/**
 * O registry parcial da PLATAFORMA — spec-001 `plataforma-por-chat`, [T015].
 *
 * Quatro famílias de entrada; o contrato (`contracts/ui-components.md` §1)
 * descreve as duas primeiras:
 *
 * 1. **As 4 tools de front** (`plataforma/navegar`, `…/abrir-cliente`,
 *    `…/preencher`, `…/destacar`) — `{executor: 'client', handler, guard, label}`.
 *    Não têm `component`: elas AGEM na tela em vez de desenhar cartão.
 * 2. **O cartão genérico delas** (`plataforma/acao-oferecida`) — o que aparece
 *    no thread quando a ação foi OFERECIDA (e não executada), quando o guard
 *    reprovou, ou quando ela já aconteceu.
 * 3. **O cartão do PRIMEIRO CONTATO** (`plataforma/boas-vindas`) — o marcador
 *    que o servidor persiste quando ele próprio resolve o turno de boas-vindas,
 *    sem chamar modelo nenhum (`core/boas_vindas_do_servidor.py`). Namespace
 *    `plataforma/` pelo mesmo motivo das duas acima: é cartão da PLATAFORMA,
 *    não de um domínio de negócio.
 * 4. **O cartão do ONBOARDING** (`plataforma/onboarding`) — as CINCO etapas da
 *    apresentação do AAWZ (`core/onboarding/`), uma chave só para as cinco
 *    (`props.etapa` diz qual). Mesmo namespace, pela mesma razão.
 *
 * ★ **Onde este parcial é registrado, e por quê não no `ui-registry/index.js`.**
 *   Quem chama `registrar(registryPlataforma)` é `modules/aawz-intelligence-v2/index.js`
 *   (a carga do módulo), e não o agregador. Motivo técnico, não estilo: os
 *   handlers importam o catálogo de navegação (`navigation-modules.js` →
 *   `navigation-routes.js` → constantes de uma dúzia de módulos), e
 *   `ui-registry/index.js` é importado por `api/apiChat.js` — que está no grafo
 *   de `apiFeeFixoIa.js` (`class APIFeeFixoIa extends APIChat`). Pôr o catálogo
 *   nesse grafo é o caminho de volta ao ciclo que derrubou 8 suítes em 26/08
 *   ([T010]). O `apiChat` importa daqui apenas `resolverEntrada` e a tabela de
 *   chaves, que não puxam nada.
 *
 * ★ **O schema é inline, e não um arquivo em `ui-registry/schemas/`.** Aqueles
 *   são cópias byte-a-byte dos contratos do backend ([T020]); este cartão não
 *   tem contraparte lá — as tools de front declaram `UI_SCHEMA = None` no
 *   backend justamente porque o `ui` delas nasce AQUI, montado pelo parser.
 *   Um arquivo em `schemas/` prometeria um espelho que não existe.
 */
import { TOOL_PART_STATE } from '@modules/aawz-intelligence-v2/store/toolParts'
import {
  chaveDaToolDeFront,
  CHAVE_ACAO_OFERECIDA,
  MODULO_DA_PLATAFORMA,
} from './chave-de-tool-de-front'
import { CHAVE_DO_CARTAO_DE_BOAS_VINDAS } from './chave-da-boas-vindas'
import { CHAVE_DO_CARTAO_DE_ONBOARDING } from './chave-do-onboarding'
import { TOOLS_DE_FRONT } from './tools-de-front'
import { registrar } from './index'

/** Os estados que o cartão sabe desenhar (o parser carimba um destes). */
export const ESTADOS_DA_ACAO_OFERECIDA = [
  TOOL_PART_STATE.ENTRADA,
  TOOL_PART_STATE.SAIDA,
  TOOL_PART_STATE.RECUSA,
  TOOL_PART_STATE.ERRO,
]

export const LABEL_DA_ACAO_OFERECIDA = 'Ação na plataforma'

/**
 * O contrato dos props do cartão. `namespace` está aqui porque o cartão precisa
 * saber por qual instância de chat devolver o `tool_output` — e
 * `additionalProperties: false` recusaria a chave se ela não fosse declarada.
 *
 * ★ `bloqueado`/`href` [feedback do owner, 04/09]: quando o `window.open`
 * automático é barrado pelo navegador, o cartão precisa do `href` para
 * oferecer o botão "Abrir em nova aba". Faltando aqui, `additionalProperties:
 * false` rejeitava o objeto de props inteiro (não só os dois campos novos) e
 * o cartão caía no fallback — regressão medida: "ação JÁ EXECUTADA" parou de
 * mostrar o selo "Aberto" e a proposta viva perdeu um dos dois botões, porque
 * NENHUMA variante do cartão validava mais contra o schema.
 */
export const SCHEMA_DA_ACAO_OFERECIDA = {
  type: 'object',
  required: ['tool'],
  properties: {
    tool: { type: 'string' },
    label: { type: 'string' },
    input: { type: 'object' },
    motivo: { type: 'string' },
    namespace: { type: 'string' },
    bloqueado: { type: 'boolean' },
    href: { type: 'string' },
  },
  additionalProperties: false,
}

/**
 * O único estado que o cartão de boas-vindas desenha.
 *
 * O turno do primeiro contato é resolvido INTEIRO pelo servidor — não há
 * esqueleto, não há aprovação e não há erro a desenhar: quando a parte chega,
 * ela já vem em `output-available` (`core/boas_vindas_do_servidor.
 * cartao_da_boas_vindas` carimba o estado à mão). Declarar estados que ele não
 * sabe desenhar faria o registry aceitar uma parte que viraria cartão vazio; é
 * para isso que `states` existe.
 */
export const ESTADOS_DA_BOAS_VINDAS = [TOOL_PART_STATE.SAIDA]

export const LABEL_DA_BOAS_VINDAS = 'Boas-vindas ao AAWZ'

/**
 * O contrato dos props do cartão de boas-vindas.
 *
 * ★ Inline, e não um arquivo em `ui-registry/schemas/`, pela MESMA razão do
 * `acao-oferecida` acima: não existe contraparte em `aawz_intelligence_v2/
 * contratos/`. O cartão não nasce de uma tool — nenhum modelo o chama, o
 * `ToolRegistry` não o oferece, e por isso não há `UI_SCHEMA` a espelhar. Um
 * arquivo em `schemas/` prometeria um espelho que não existe, e a próxima
 * pessoa iria procurá-lo no backend.
 *
 * ⚠️ **Sem `additionalProperties: false`, de propósito.** O contrato deste
 * turno ainda está sendo fechado do lado do servidor. Com a porta trancada, um
 * campo NOVO no `props` não reprovaria só aquele campo — reprovaria o objeto
 * inteiro, e a apresentação da casa cairia no `fallback-card` no primeiro
 * contato de quem acabou de chegar. É a regressão medida em 04/09 no
 * `acao-oferecida` (`bloqueado`/`href`), e o custo de repeti-la aqui é mais
 * alto: lá o consultor perdia um botão; aqui ele perde a primeira impressão.
 * Fechar a porta é o passo certo DEPOIS que o backend congelar o shape.
 *
 * `versao_roteiro` não é `required` pelo mesmo motivo — e porque o front não
 * depende dele para desenhar: o roteiro é DAQUI (`roteiro.js`), e a versão é o
 * que o servidor sabe e o front não tem como derivar sozinho.
 */
export const SCHEMA_DA_BOAS_VINDAS = {
  type: 'object',
  properties: {
    versao_roteiro: { type: 'string' },
  },
}

/**
 * O único estado que o cartão de onboarding desenha — mesma razão da
 * boas-vindas logo acima: o turno é resolvido INTEIRO pelo servidor
 * (`core/onboarding/cartoes.py::cartao_da_etapa` carimba
 * `state: 'output-available'` à mão), então não há esqueleto, não há aprovação
 * e não há erro a desenhar.
 */
export const ESTADOS_DO_ONBOARDING = [TOOL_PART_STATE.SAIDA]

export const LABEL_DO_ONBOARDING = 'Primeiros passos no AAWZ'

// Os ids das cinco etapas — espelho MANUAL de `core/onboarding/roteiro.py`
// (`IDS_DAS_ETAPAS`), NA ORDEM. É o enum que reprova uma etapa que saiu do
// roteiro do servidor antes de ela virar um cartão sem corpo na tela.
const ETAPAS_DO_ONBOARDING = [
  'conhecer_escritorio',
  'dia_a_dia',
  'conectar_ferramentas',
  'conhecer_skills',
  'rodar_skill',
]

const ITEM_PERGUNTA = {
  type: 'object',
  properties: {
    id: { type: 'string' },
    pergunta: { type: 'string' },
    ajuda: { type: 'string' },
  },
  required: ['id', 'pergunta'],
  additionalProperties: false,
}

// ★ `descricao` é `required` aqui, ao contrário do que acontece no
// `ITEM_SKILL` logo abaixo. A diferença é de ORIGEM: as skills vêm de um
// catálogo real, onde uma entrada pode não ter descrição publicada; as quatro
// prioridades são uma constante do servidor (`prioridades.py`), escritas à mão
// com a linha de apoio — sem ela, o cartão desenha um título solto e a pessoa
// não sabe o que a IA passaria a fazer por ela.
const ITEM_PRIORIDADE = {
  type: 'object',
  properties: {
    id: { type: 'string' },
    titulo: { type: 'string' },
    descricao: { type: 'string' },
  },
  required: ['id', 'titulo', 'descricao'],
  additionalProperties: false,
}

const ITEM_CONECTOR = {
  type: 'object',
  properties: {
    id: { type: 'string' },
    titulo: { type: 'string' },
    categoria: { type: 'string' },
    rota: { type: 'string' },
    // Vocabulário FECHADO (`conectores.py`). `desconhecido` é o estado da
    // leitura que FALHOU, e ele precisa chegar ao cartão: é o que faz a linha
    // aparecer sem o marcador e sem o convite, em vez de afirmar que o
    // conector está desligado.
    estado: { enum: ['conectado', 'desconectado', 'desconhecido'] },
  },
  required: ['id', 'titulo', 'estado'],
  additionalProperties: false,
}

const ITEM_SKILL = {
  type: 'object',
  properties: {
    slug: { type: 'string' },
    titulo: { type: 'string' },
    descricao: { type: 'string' },
  },
  required: ['slug', 'titulo'],
  additionalProperties: false,
}

/**
 * O contrato dos props do cartão de onboarding.
 *
 * ★ Inline pela MESMA razão das duas famílias acima: não existe contraparte em
 * `aawz_intelligence_v2/contratos/`. O cartão não nasce de uma tool — nenhum
 * modelo o chama, o `ToolRegistry` nunca o oferece (`cartoes.py`:
 * *"`NOME_DA_MARCA` não é (e não pode ser) uma tool registrada"*) —, logo não
 * há `UI_SCHEMA` a espelhar. Um arquivo em `schemas/` prometeria um espelho
 * que não existe.
 *
 * ★ **`anyOf`, um ramo por etapa, é o que torna o schema uma régua de verdade.**
 * Os cinco campos COMUNS são `required` no topo; o campo ESPECÍFICO (as
 * perguntas, os conectores, as skills, o atalho) é exigido no ramo da etapa
 * que o usa — `_props_da_etapa` (`cartoes.py`) manda um por vez, e é por isso
 * que declará-los todos opcionais no topo aceitaria a etapa 2 sem conectores
 * nenhum, que na tela é um cartão com cabeçalho e corpo vazio. O validador
 * deste módulo entende `anyOf`/`const`/`enum`/`items`/`required`/
 * `additionalProperties: false` (`ui-registry/validar.js`, `KEYWORDS`).
 *
 * ⚠️ **COM `additionalProperties: false`, ao contrário da boas-vindas.** Lá a
 * porta ficou aberta porque o contrato do servidor ainda estava sendo fechado;
 * aqui ele está implementado e é preciso (`core/onboarding/cartoes.py` monta
 * `props` campo a campo, e `conectores.py`/`roteiro.py` fecham os vocabulários).
 * Com a porta trancada, um campo novo do backend reprova o cartão INTEIRO e a
 * etapa cai no `fallback-card` — é o custo, e ele é assumido: o que se ganha é
 * a drift entre os dois lados virar visível na tela em vez de um campo que o
 * front ignora em silêncio. Se o backend voltar a mexer no shape, o passo certo
 * é abrir a porta aqui ANTES do deploy dele, não descobrir pelo fallback.
 */
export const SCHEMA_DO_ONBOARDING = {
  type: 'object',
  properties: {
    etapa: { enum: ETAPAS_DO_ONBOARDING },
    titulo: { type: 'string' },
    descricao: { type: 'string' },
    // `integer` e `minimum: 1`: `posicao` é `indice_da_etapa(...) + 1`, então
    // `0` só sairia de um id que não está no roteiro — e nesse caso a tela
    // diria "Etapa 0 de 4", que é pior que não desenhar.
    posicao: { type: 'integer', minimum: 1 },
    total: { type: 'integer', minimum: 1 },
    perguntas: { type: 'array', items: ITEM_PERGUNTA },
    prioridades: { type: 'array', items: ITEM_PRIORIDADE },
    conectores: { type: 'array', items: ITEM_CONECTOR },
    skills: { type: 'array', items: ITEM_SKILL },
    // O slug do atalho, SEM a barra (`'panorama'`) — a barra é apresentação, e
    // quem a escreve é o front. O `pattern` é o que impede um `/panorama` vindo
    // do fio de virar `//panorama` na tela.
    atalho: { type: 'string', pattern: '^[a-z0-9-]+$' },
  },
  required: ['etapa', 'titulo', 'descricao', 'posicao', 'total'],
  additionalProperties: false,
  anyOf: [
    {
      // ★ As perguntas VOLTARAM às props do cartão (owner, 18/09/2026, com os
      // quatro mocks na mão). Entre 18/09 e esta data elas saíram daqui: a
      // etapa 1 era só leitura e quem respondia era o formulário do canal
      // `perguntar_ao_gestor`. O desenho novo põe o campo de resposta DENTRO
      // do cartão — uma pergunta por bloco, com "Enviar respostas" no rodapé —,
      // então o cartão precisa das perguntas para desenhar o próprio corpo.
      //
      // `required` pelo MESMO motivo dos outros três ramos: sem o campo
      // específico, a etapa desenha cabeçalho e corpo vazio, que na tela é um
      // cartão que não pede nada e não diz por quê.
      properties: { etapa: { const: 'conhecer_escritorio' } },
      required: ['perguntas'],
    },
    {
      // ★ A etapa do DIA A DIA (2ª de 5, 18/09/2026). `prioridades` é
      // `required` pela MESMA razão dos irmãos: sem o campo, a etapa desenha
      // cabeçalho, pergunta e NENHUMA opção a marcar — um cartão que pede uma
      // escolha impossível, e cujo "Continuar" nasceria desabilitado para
      // sempre. Cair no `fallback-card` diz que algo quebrou; um corpo vazio
      // não diz nada.
      properties: { etapa: { const: 'dia_a_dia' } },
      required: ['prioridades'],
    },
    {
      properties: { etapa: { const: 'conectar_ferramentas' } },
      required: ['conectores'],
    },
    {
      properties: { etapa: { const: 'conhecer_skills' } },
      required: ['skills'],
    },
    {
      // ★ A etapa 4 passou a receber `skills` JUNTO de `atalho` (owner,
      // 18/09/2026): a linha do atalho mostra o comando E a descrição dele, e
      // a descrição sai do MESMO catálogo da etapa 3 — duas fontes para o
      // texto da mesma skill divergiriam na primeira revisão de copy.
      //
      // ⚠️ `skills` não entra no `required` daqui: ele é ENRIQUECIMENTO, não o
      // campo que dá corpo à etapa. Sem ele o cartão desenha só o comando e o
      // botão, que continua sendo uma etapa 4 inteira; sem `atalho` não há o
      // que rodar. Aceitá-lo não custou linha de schema — `skills` já está
      // declarado no `properties` do topo, e é de lá que o
      // `additionalProperties: false` o reconhece —, mas o ramo declara a
      // intenção para quem vier ler por que a etapa 4 recebe dois campos.
      properties: { etapa: { const: 'rodar_skill' } },
      required: ['atalho'],
    },
  ],
}

const entradaDaTool = (tool) => ({
  executor: 'client',
  handler: tool.executar,
  guard: tool.guard,
  label: tool.label,
})

const parcial = {
  [CHAVE_ACAO_OFERECIDA]: {
    component: () => import('./acao-oferecida-card.vue'),
    schema: () => Promise.resolve(SCHEMA_DA_ACAO_OFERECIDA),
    module: MODULO_DA_PLATAFORMA,
    states: ESTADOS_DA_ACAO_OFERECIDA,
    label: LABEL_DA_ACAO_OFERECIDA,
  },
  // ★ O primeiro contato como CARTÃO do turno (`core/boas_vindas_do_servidor.py`).
  // O componente é a casca fina sobre o player que já existia — ver
  // `cartoes/cartao-boas-vindas.vue` para o porquê de não registrar o player
  // direto.
  [CHAVE_DO_CARTAO_DE_BOAS_VINDAS]: {
    component: () => import('./cartoes/cartao-boas-vindas.vue'),
    schema: () => Promise.resolve(SCHEMA_DA_BOAS_VINDAS),
    module: MODULO_DA_PLATAFORMA,
    states: ESTADOS_DA_BOAS_VINDAS,
    label: LABEL_DA_BOAS_VINDAS,
  },
  // ★ As quatro etapas da apresentação, numa chave só (`core/onboarding/`).
  // Uma entrada por etapa faria o registry apontar quatro componentes que
  // dividem casca, cabeçalho e rodapé — ver o `★` do topo de
  // `cartoes/cartao-onboarding.vue`.
  [CHAVE_DO_CARTAO_DE_ONBOARDING]: {
    component: () => import('./cartoes/cartao-onboarding.vue'),
    schema: () => Promise.resolve(SCHEMA_DO_ONBOARDING),
    module: MODULO_DA_PLATAFORMA,
    states: ESTADOS_DO_ONBOARDING,
    label: LABEL_DO_ONBOARDING,
  },
}

TOOLS_DE_FRONT.forEach((tool) => {
  parcial[chaveDaToolDeFront(tool.nome)] = entradaDaTool(tool)
})

/**
 * Agrega este parcial no `UI_REGISTRY`. Idempotente: `registrar` recusa chave
 * repetida (dois módulos disputando a mesma chave se sobrescreveriam em
 * silêncio), então registrar duas vezes precisa ser um no-op explícito.
 */
export function registrarPlataforma() {
  if (registrarPlataforma.feito) return
  registrarPlataforma.feito = true
  registrar(parcial)
}

export default parcial
